import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import { solicitudDetalleService } from '../../services';

const DEFAULT_AUTH_API_URL = "http://localhost:8080";

const getAuthApiUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return ''; // Use relative path for proxy in development
  }
  const raw =
    (typeof window !== 'undefined' && window.env?.REACT_APP_AUTH_API_URL) ||
    (typeof window !== 'undefined' && window.env?.REACT_APP_API_URL) ||
    process.env.REACT_APP_AUTH_API_URL ||
    process.env.REACT_APP_API_URL ||
    DEFAULT_AUTH_API_URL;
  return raw.endsWith("/") ? raw.slice(0, -1) : raw;
};

const AUTH_API_URL = getAuthApiUrl();

const tipoIDOptions = [
  { value: 'DNI', label: 'DNI' },
  { value: 'LC', label: 'LC' },
  { value: 'LE', label: 'LE' },
  { value: 'PASAPORTE', label: 'PASAPORTE' },
  { value: 'CI', label: 'CI' }
];

const sexoOptions = [
  { value: 'MASCULINO', label: 'Masculino' },
  { value: 'FEMENINO', label: 'Femenino' },
  { value: 'OTRO', label: 'Otro' }
];

const estadoCivilOptions = [
  { value: 'SOLTERO', label: 'Soltero' },
  { value: 'CASADO', label: 'Casado' },
  { value: 'DIVORCIADO', label: 'Divorciado' },
  { value: 'VIUDO', label: 'Viudo' },
  { value: 'CONCUBINATO', label: 'Concubinato' }
];

const SeccionDatosPersonalesIndividuo = ({ solicitudId }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dniArchivos, setDniArchivos] = useState({
    frente: null,
    reverso: null
  });
  const [formData, setFormData] = useState({
    tipoID: 'DNI',
    idNumero: '',
    fechaNacimiento: '',
    lugarNacimiento: '',
    nacionalidad: 'ARGENTINA',
    paisOrigen: 'ARGENTINA',
    paisResidencia: 'ARGENTINA',
    actividad: '',
    sexo: 'MASCULINO',
    estadoCivil: 'SOLTERO',
    dniFrenteArchivoId: null,
    dniReversoArchivoId: null,
    conyuge: {
      nombres: '',
      apellidos: '',
      tipoID: 'DNI',
      idNumero: '',
      tipoClaveFiscal: 'CUIT',
      claveFiscal: ''
    }
  });

  const cargarArchivoDNI = async (archivoId, side) => {
    if (!archivoId) return;
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${AUTH_API_URL}/api/archivos/${archivoId}`, {
        method: 'GET',
        headers: {
          'Accept': '*/*',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        
        // Convertir archivoData (base64) a blob
        const binaryString = atob(result.archivoData);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes]);
        const fileUrl = URL.createObjectURL(blob);
        
        setDniArchivos(prev => ({
          ...prev,
          [side]: {
            id: archivoId,
            nombre: result.nombre || `DNI ${side === 'frente' ? 'Frente' : 'Dorso'}`,
            size: blob.size || 0,
            url: fileUrl
          }
        }));
      }
    } catch (err) {
      console.error(`Error cargando archivo DNI ${side}:`, err);
    }
  };

  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await solicitudDetalleService.getDatosPersonalesIndividuo(solicitudId);
      if (response) {
        setFormData({
          tipoID: response.tipoID || 'DNI',
          idNumero: response.idNumero || '',
          fechaNacimiento: response.fechaNacimiento || '',
          lugarNacimiento: response.lugarNacimiento || '',
          nacionalidad: response.nacionalidad || 'ARGENTINA',
          paisOrigen: response.paisOrigen || 'ARGENTINA',
          paisResidencia: response.paisResidencia || 'ARGENTINA',
          actividad: response.actividad || '',
          sexo: response.sexo || 'MASCULINO',
          estadoCivil: response.estadoCivil || 'SOLTERO',
          dniFrenteArchivoId: response.dniFrenteArchivoId || null,
          dniReversoArchivoId: response.dniReversoArchivoId || null,
          conyuge: response.conyuge || {
            nombres: '',
            apellidos: '',
            tipoID: 'DNI',
            idNumero: '',
            tipoClaveFiscal: 'CUIT',
            claveFiscal: ''
          }
        });
        
        // Cargar información de los archivos del DNI
        setDniArchivos({ frente: null, reverso: null });
        if (response.dniFrenteArchivoId) {
          await cargarArchivoDNI(response.dniFrenteArchivoId, 'frente');
        }
        if (response.dniReversoArchivoId) {
          await cargarArchivoDNI(response.dniReversoArchivoId, 'reverso');
        }
      }
    } catch (err) {
      setError('Error al cargar los datos personales');
    } finally {
      setLoading(false);
    }
  }, [solicitudId]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const handleInputChange = (field, value) => {
    if (field === 'idNumero') {
      const numericValue = value.replace(/[^0-9]/g, '');
      setFormData(prev => ({ ...prev, [field]: numericValue }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleConyugeChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      conyuge: {
        ...prev.conyuge,
        [field]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const datosParaGuardar = {
        solicitudId: parseInt(solicitudId),
        ...formData
      };
      await solicitudDetalleService.saveDatosPersonalesIndividuo(datosParaGuardar);
      setSuccess('Datos guardados exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error al guardar los datos');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Datos Personales
        </Typography>
        {error && (
          <Box p={2} mb={2} style={{ backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px' }}>
            <Typography variant="body2">{error}</Typography>
          </Box>
        )}
        {success && (
          <Box p={2} mb={2} style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: '4px' }}>
            <Typography variant="body2">{success}</Typography>
          </Box>
        )}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel>Tipo de Documento</InputLabel>
                <Select
                  value={formData.tipoID}
                  onChange={(e) => handleInputChange('tipoID', e.target.value)}
                  label="Tipo de Documento"
                >
                  {tipoIDOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Número de Documento"
                required
                value={formData.idNumero}
                onChange={(e) => handleInputChange('idNumero', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fecha de Nacimiento"
                type="date"
                value={formData.fechaNacimiento}
                onChange={(e) => handleInputChange('fechaNacimiento', e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Lugar de Nacimiento"
                required
                value={formData.lugarNacimiento}
                onChange={(e) => handleInputChange('lugarNacimiento', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nacionalidad"
                required
                value={formData.nacionalidad}
                onChange={(e) => handleInputChange('nacionalidad', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="País de Origen"
                required
                value={formData.paisOrigen}
                onChange={(e) => handleInputChange('paisOrigen', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="País de Residencia"
                required
                value={formData.paisResidencia}
                onChange={(e) => handleInputChange('paisResidencia', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Actividad"
                required
                value={formData.actividad}
                onChange={(e) => handleInputChange('actividad', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel>Sexo</InputLabel>
                <Select
                  value={formData.sexo}
                  onChange={(e) => handleInputChange('sexo', e.target.value)}
                  label="Sexo"
                >
                  {sexoOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel>Estado Civil</InputLabel>
                <Select
                  value={formData.estadoCivil}
                  onChange={(e) => handleInputChange('estadoCivil', e.target.value)}
                  label="Estado Civil"
                >
                  {estadoCivilOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {(dniArchivos.frente || dniArchivos.reverso) && (
              <Grid item xs={12}>
                <Typography variant="h6" style={{ marginTop: '1rem', marginBottom: '1rem', fontWeight: 'bold' }}>
                  Adjuntos del DNI
                </Typography>
                {dniArchivos.frente && (
                  <Card style={{ marginBottom: '1.5rem', border: '1px solid #e0e0e0', backgroundColor: '#f9fafb' }}>
                    <CardContent>
                      <Box display="flex" flexDirection="column" alignItems="flex-start" gap={1}>
                        <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                          DNI Frente
                        </Typography>
                        <Box display="flex" flexDirection="column" marginTop={1}>
                          <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                            {dniArchivos.frente.nombre}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {Math.round((dniArchivos.frente.size || 0) / 1024)} KB
                          </Typography>
                          {dniArchivos.frente.url && (
                            <Box display="flex" gap={1} marginTop={1}>
                              <Button
                                variant="text"
                                color="primary"
                                onClick={() => window.open(dniArchivos.frente.url, '_blank')}
                                size="small"
                              >
                                Ver documento
                              </Button>
                              <Button
                                variant="text"
                                color="primary"
                                startIcon={<GetAppIcon />}
                                onClick={() => {
                                  const link = document.createElement('a');
                                  link.href = dniArchivos.frente.url;
                                  link.download = dniArchivos.frente.nombre || 'dni-frente.pdf';
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }}
                                size="small"
                              >
                                Descargar
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                )}
                {dniArchivos.reverso && (
                  <Card style={{ marginBottom: '1.5rem', border: '1px solid #e0e0e0', backgroundColor: '#f9fafb' }}>
                    <CardContent>
                      <Box display="flex" flexDirection="column" alignItems="flex-start" gap={1}>
                        <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                          DNI Dorso
                        </Typography>
                        <Box display="flex" flexDirection="column" marginTop={1}>
                          <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                            {dniArchivos.reverso.nombre}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {Math.round((dniArchivos.reverso.size || 0) / 1024)} KB
                          </Typography>
                          {dniArchivos.reverso.url && (
                            <Box display="flex" gap={1} marginTop={1}>
                              <Button
                                variant="text"
                                color="primary"
                                onClick={() => window.open(dniArchivos.reverso.url, '_blank')}
                                size="small"
                              >
                                Ver documento
                              </Button>
                              <Button
                                variant="text"
                                color="primary"
                                startIcon={<GetAppIcon />}
                                onClick={() => {
                                  const link = document.createElement('a');
                                  link.href = dniArchivos.reverso.url;
                                  link.download = dniArchivos.reverso.nombre || 'dni-dorso.pdf';
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }}
                                size="small"
                              >
                                Descargar
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Grid>
            )}
            {formData.estadoCivil === 'CASADO' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Datos del Cónyuge
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nombres del Cónyuge"
                    value={formData.conyuge.nombres}
                    onChange={(e) => handleConyugeChange('nombres', e.target.value)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Apellidos del Cónyuge"
                    value={formData.conyuge.apellidos}
                    onChange={(e) => handleConyugeChange('apellidos', e.target.value)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="DNI del Cónyuge"
                    value={formData.conyuge.idNumero}
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(/[^0-9]/g, '');
                      handleConyugeChange('idNumero', numericValue);
                    }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Clave Fiscal del Cónyuge"
                    value={formData.conyuge.claveFiscal}
                    onChange={(e) => handleConyugeChange('claveFiscal', e.target.value)}
                    variant="outlined"
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
              >
                {saving ? <CircularProgress size={20} /> : 'Guardar'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );
};

export default SeccionDatosPersonalesIndividuo;

